// The only network layer. Pages import hooks from here or from the module
// files beside it; they never call fetch themselves. Types come from the
// contract (src/api/generated/schema.d.ts, regenerated by `pnpm run gen:api`).
import createClient, { type Middleware } from 'openapi-fetch'
import type { components, paths } from './generated/schema'
import { readToken, clearToken } from './session'

export type ApiError = components['schemas']['Error']
export type Me = components['schemas']['Me']
export type Session = components['schemas']['Session']

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const token = readToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
  onResponse({ response }) {
    if (response.status === 401 && readToken()) {
      // The server says the session is gone; drop it so the app re-routes to login.
      clearToken()
    }
    return response
  },
}

export const api = createClient<paths>({ baseUrl: '/v1' })
api.use(authMiddleware)

// Turn an openapi-fetch result into a thrown ApiError-shaped failure so
// TanStack Query sees one error type.
export class RequestFailed extends Error {
  readonly status: number
  readonly code: string
  readonly fields: Record<string, string> | undefined
  constructor(status: number, body: ApiError | undefined) {
    super(body?.message ?? `The server answered ${status} without a message.`)
    this.status = status
    this.code = body?.code ?? 'unknown'
    this.fields = body?.fields
  }
}

export function unwrap<T>(result: { data?: T; error?: ApiError; response: Response }): T {
  if (result.error || !result.response.ok) {
    throw new RequestFailed(result.response.status, result.error)
  }
  return result.data as T
}
